import { useQuery } from '@tanstack/react-query';
import { getCurrentUser } from '@aws-amplify/auth';
import {
	decodeTokenPayload,
	getAccessTokenWithFallback
} from 'features/communication-bridge/tokenStore';

/**
 * Resolves the current user from the shell-injected token when available,
 * falling back to Amplify's `getCurrentUser()` for standalone / test usage.
 */
export const useCurrentUserQuery = () =>
	useQuery({
		queryKey: ['currentUser'],
		queryFn: async () => {
			const token = await getAccessTokenWithFallback();
			if (token) {
				const payload = decodeTokenPayload();
				if (!payload) return null;
				return {
					userId: payload.sub as string,
					username: (payload['cognito:username'] as string) ?? (payload.sub as string),
					signInDetails: undefined
				};
			}

			try {
				const user = await getCurrentUser();
				return {
					userId: user.userId,
					username: user.username,
					signInDetails: user.signInDetails
				};
			} catch {
				return null;
			}
		},
		retry: false,
		refetchInterval: 30_000
	});

export type CurrentUserQuery = ReturnType<typeof useCurrentUserQuery>;
